import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LogOut, Music, Calendar, Users, Home, Mic, Mail,
  MessageSquare, ShoppingBag, Image, Share2, ChevronDown, FileText,
  Inbox, FolderOpen, UserCheck, Shield, Radio, Link2
} from 'lucide-react';
import ArtistsTabs from './ArtistsTabs';
import EventManagement from './EventManagement';
import ReleaseManagement from './ReleaseManagement';
import AppearanceManagement from './AppearanceManagement';
import NewsletterManagement from './NewsletterManagement';
import ContactManagement from './ContactManagement';
import TrackManagement from './TrackManagement';
import CoverArtGenerator from './CoverArtGenerator';
import StaffSocialHub from './StaffSocialHub';
import DocumentTemplates from './DocumentTemplates';
import StaffReviewStation from './StaffReviewStation';
import ArtistInvites from './ArtistInvites';
import UserManagement from './UserManagement';
import FileBrowser from './FileBrowser';
import PromoManagement from './PromoManagement';
import AMRLinksManager from './AMRLinksManager';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NavDropdown = ({ label, icon: Icon, items, location, openKey, setOpenKey, navKey }) => {
  const ref = useRef(null);
  const isOpen = openKey === navKey;
  const isActive = items.some(item => location.pathname.includes(item.path));

  const toggle = () => setOpenKey(isOpen ? null : navKey);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        if (isOpen) setOpenKey(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setOpenKey]);

  return (
    <div className={`nav-dropdown ${isActive ? 'active' : ''}`} ref={ref} data-testid={`nav-group-${navKey}`}>
      <button className="nav-dropdown-trigger" onClick={toggle}>
        <Icon size={16} />
        <span>{label}</span>
        <ChevronDown size={14} className={`chevron ${isOpen ? 'open' : ''}`} />
      </button>
      {isOpen && (
        <div className="nav-dropdown-menu">
          {items.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`dropdown-item ${location.pathname.includes(item.path) ? 'active' : ''}`}
              onClick={() => setOpenKey(null)}
              data-testid={`nav-${item.testId}`}
            >
              <item.icon size={15} />
              <span>{item.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) { navigate('/admin/login'); return; }
      try {
        const response = await fetch(`${API_URL}/api/admin/appearances`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          navigate('/admin/login');
          return;
        }
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Token verification failed:', error);
        setIsAuthenticated(true);
      } finally {
        setIsVerifying(false);
      }
    };
    verifyToken();
  }, [navigate]);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); setOpenDropdown(null); }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  if (isVerifying) {
    return (
      <div className="admin-dashboard" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <p>Verifying session...</p>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const navGroups = {
    artists: {
      label: 'Artists',
      icon: Users,
      items: [
        { path: '/admin/artists', label: 'Artist Profiles', icon: FolderOpen, testId: 'artist-profiles' },
      ]
    },
    roster: {
      label: 'Roster',
      icon: UserCheck,
      items: [
        { path: '/admin/roster', label: 'Demo Submissions', icon: Inbox, testId: 'demos' },
        { path: '/admin/roster-invites', label: 'Invite Links', icon: Mail, testId: 'invites' },
      ]
    },
    music: {
      label: 'Music',
      icon: Music,
      items: [
        { path: '/admin/releases', label: 'Releases', icon: Music, testId: 'releases' },
        { path: '/admin/promos', label: 'Promo Pages', icon: Radio, testId: 'promos' },
        { path: '/admin/tracks', label: 'Store Tracks', icon: ShoppingBag, testId: 'store-tracks' },
        { path: '/admin/cover-art', label: 'Cover Art', icon: Image, testId: 'cover-art' },
      ]
    },
    events: {
      label: 'Events',
      icon: Calendar,
      items: [
        { path: '/admin/events', label: 'Events', icon: Calendar, testId: 'events' },
        { path: '/admin/appearances', label: 'Appearances', icon: Mic, testId: 'appearances' },
      ]
    },
    comms: {
      label: 'Comms',
      icon: Mail,
      items: [
        { path: '/admin/newsletter', label: 'Newsletter', icon: Mail, testId: 'newsletter' },
        { path: '/admin/messages', label: 'Messages', icon: MessageSquare, testId: 'messages' },
        { path: '/admin/social-hub', label: 'Social Hub', icon: Share2, testId: 'social-hub' },
        { path: '/admin/amr-links', label: 'AMR Links', icon: Link2, testId: 'amr-links' },
      ]
    },
    admin: {
      label: 'Admin',
      icon: Shield,
      items: [
        { path: '/admin/users', label: 'User Management', icon: Shield, testId: 'user-mgmt' },
        { path: '/admin/files', label: 'File Browser', icon: FolderOpen, testId: 'file-browser' },
      ]
    }
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-topbar" data-testid="admin-topbar">
        <div className="topbar-left">
          <Link to="/admin/dashboard" className="topbar-brand">
            <img src="/amr-logo.png" alt="AMR" className="topbar-logo" />
            <span className="topbar-title">Admin</span>
          </Link>
        </div>

        <button
          className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>

        <nav className={`topbar-nav ${mobileMenuOpen ? 'mobile-open' : ''}`} data-testid="topbar-nav">
          <Link
            to="/admin/dashboard"
            className={`nav-link ${location.pathname === '/admin/dashboard' ? 'active' : ''}`}
            data-testid="nav-dashboard"
          >
            <Home size={16} />
            <span>Dashboard</span>
          </Link>

          {Object.entries(navGroups).map(([key, group]) => (
            <NavDropdown
              key={key}
              navKey={key}
              label={group.label}
              icon={group.icon}
              items={group.items}
              location={location}
              openKey={openDropdown}
              setOpenKey={setOpenDropdown}
            />
          ))}

          <Link
            to="/admin/templates"
            className={`nav-link ${location.pathname.includes('/admin/templates') ? 'active' : ''}`}
            data-testid="nav-templates"
          >
            <FileText size={16} />
            <span>Templates</span>
          </Link>

          <button onClick={handleLogout} className="nav-logout" data-testid="admin-logout">
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </nav>
      </header>

      <main className="admin-main" data-testid="admin-main-content">
        <Routes>
          <Route path="dashboard" element={<DashboardHome />} />
          <Route path="artists" element={<ArtistsTabs />} />
          <Route path="roster" element={<StaffReviewStation />} />
          <Route path="roster-invites" element={<ArtistInvites />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="files" element={<FileBrowser />} />
          <Route path="templates" element={<DocumentTemplates />} />
          <Route path="releases" element={<ReleaseManagement />} />
          <Route path="promos" element={<PromoManagement />} />
          <Route path="tracks" element={<TrackManagement />} />
          <Route path="events" element={<EventManagement />} />
          <Route path="appearances" element={<AppearanceManagement />} />
          <Route path="newsletter" element={<NewsletterManagement />} />
          <Route path="messages" element={<ContactManagement />} />
          <Route path="cover-art" element={<CoverArtGenerator />} />
          <Route path="social-hub" element={<StaffSocialHub />} />
          <Route path="amr-links" element={<AMRLinksManager />} />
        </Routes>
      </main>
    </div>
  );
};

const DashboardHome = () => {
  return (
    <div className="dashboard-home" data-testid="admin-dashboard-home">
      <h1>Welcome to AMR Admin Portal</h1>
      <p>Select a section from the top menu to manage your content</p>

      <div className="dashboard-cards">
        <Link to="/admin/artists" className="dashboard-card highlight" data-testid="card-artists">
          <Users size={32} />
          <h3>Artist Management</h3>
          <p>Public profiles, artist profiles, invites & demos</p>
        </Link>

        <Link to="/admin/releases" className="dashboard-card" data-testid="card-releases">
          <Music size={32} />
          <h3>Releases</h3>
          <p>Add and update music releases</p>
        </Link>

        <Link to="/admin/promos" className="dashboard-card" data-testid="card-promos">
          <Music size={32} />
          <h3>Promo Pages</h3>
          <p>Create trackable promo links for DJs</p>
        </Link>

        <Link to="/admin/events" className="dashboard-card" data-testid="card-events">
          <Calendar size={32} />
          <h3>Events</h3>
          <p>Create events and view registrations</p>
        </Link>

        <Link to="/admin/appearances" className="dashboard-card" data-testid="card-appearances">
          <Mic size={32} />
          <h3>Appearances</h3>
          <p>External events where artists perform</p>
        </Link>

        <Link to="/admin/newsletter" className="dashboard-card" data-testid="card-newsletter">
          <Mail size={32} />
          <h3>Newsletter</h3>
          <p>View and export newsletter subscribers</p>
        </Link>

        <Link to="/admin/messages" className="dashboard-card" data-testid="card-messages">
          <MessageSquare size={32} />
          <h3>Messages</h3>
          <p>View and respond to contact form submissions</p>
        </Link>

        <Link to="/admin/cover-art" className="dashboard-card" data-testid="card-cover-art">
          <Image size={32} />
          <h3>Cover Art Generator</h3>
          <p>Create AI-generated album artwork</p>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
